import React from 'react';
import { useTranslation } from 'react-i18next';
import type { FormErrors } from '@jwp/ott-common/types/form';
import type { ValidateQRLoginFormData } from '@jwp/ott-common/types/account';
import { testId } from '@jwp/ott-common/src/utils/common';
import { simultaneousLoginWarningKey } from '@jwp/ott-common/src/constants';
import TextField from '../TextField/TextField';
import Button from '../Button/Button';
import FormFeedback from '../FormFeedback/FormFeedback';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';

import styles from './ValidateQRLoginForm.module.scss';

type Props = {
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  error?: string;
  errors: FormErrors<ValidateQRLoginFormData>;
  values: ValidateQRLoginFormData;
  validationError?: boolean;
  isQrRedirection: boolean;
  submitting: boolean;
  siteName?: string;
  messageKey: string | null;
};

const ValidateQRLoginForm: React.FC<Props> = ({ onSubmit, onChange, values, errors, validationError, isQrRedirection, submitting, messageKey }: Props) => {
  const { t } = useTranslation('account');

  const getTranslatedErrorMessage = (messageId: string | null) => {
    if (messageId === simultaneousLoginWarningKey) {
      return t('login.simultaneous_logins');
    }
    return t('login.unexpected_error');
  };

  return (
    <form onSubmit={onSubmit} data-testid={testId('login-form')} noValidate>
      {messageKey && (
        <div className={styles.top}>
          <FormFeedback variant="warning">{getTranslatedErrorMessage(messageKey)}</FormFeedback>
        </div>
      )}
      {errors.form ? (
        <FormFeedback variant="error" visible={!validationError}>
          {errors.form}
        </FormFeedback>
      ) : null}

      <h2 className={styles.title}>{t('login.sign_in_with_qr_code')}</h2>
      <TextField
        value={values.QrCode}
        onChange={onChange}
        label={t('login.qr_code')}
        placeholder={t('login.qr_code')}
        className={styles.otpInput}
        error={!!errors.QrCode}
        helperText={errors.QrCode}
        name="QrCode"
        maxLength={6}
        type="otp"
        required
        otpMode={true}
        testId="login-qr-validation-input"
      />
      {(submitting || isQrRedirection) && <LoadingOverlay transparentBackground inline />}
      <Button type="submit" label={t('login.sign_in_with_qr_code')} variant="contained" color="primary" size="large" disabled={submitting} fullWidth />
    </form>
  );
};

export default ValidateQRLoginForm;
